import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, ExternalLink, ArrowLeft, Star, Package, Zap, Minus, Plus, Share2, Truck, Globe, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';
import ProductMediaGallery from '@/components/ProductMediaGallery';
import WishlistButton from '@/components/WishlistButton';
import WhatsAppContactButton from '@/components/WhatsAppContactButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loading Skeleton Component
const ProductSkeleton = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="grid lg:grid-cols-2 gap-12">
      <div className="aspect-square skeleton-pulse rounded-lg"></div>
      <div className="space-y-4">
        <div className="h-8 skeleton-pulse w-3/4"></div>
        <div className="h-4 skeleton-pulse w-1/2"></div>
        <div className="h-24 skeleton-pulse"></div>
      </div>
    </div>
  </div>
);

// Not Found Component
const ProductNotFound = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
    <h2 className="text-2xl font-bold mb-4">Product not found</h2>
    <Link to="/products">
      <Button>Back to Products</Button>
    </Link>
  </div>
);

// Quantity Selector Component
const QuantitySelector = ({ quantity, setQuantity }) => (
  <div className="flex items-center border rounded-md">
    <Button 
      variant="ghost" 
      size="icon"
      onClick={() => setQuantity(Math.max(1, quantity - 1))}
      disabled={quantity <= 1}
      data-testid="decrease-quantity"
    >
      <Minus className="w-4 h-4" />
    </Button>
    <span className="w-12 text-center font-medium" data-testid="quantity-display">{quantity}</span>
    <Button 
      variant="ghost" 
      size="icon"
      onClick={() => setQuantity(quantity + 1)}
      data-testid="increase-quantity"
    >
      <Plus className="w-4 h-4" />
    </Button>
  </div>
);

// Size Selector Component
const SizeSelector = ({ sizes, selectedSize, onSelect }) => {
  if (!sizes || sizes.length === 0) return null;
  
  return (
    <div className="space-y-3" data-testid="size-selector">
      <h4 className="font-semibold text-sm" style={{ fontFamily: 'Archivo, sans-serif' }}>
        Size <span className="text-destructive">*</span>
      </h4>
      <div className="flex flex-wrap gap-2">
        {sizes.map((size) => (
          <button
            key={size}
            onClick={() => onSelect(size)}
            className={`px-4 py-2 rounded-md border-2 text-sm font-medium transition-all ${
              selectedSize === size
                ? 'border-foreground bg-foreground text-background'
                : 'border-border hover:border-foreground/50'
            }`}
            data-testid={`size-option-${size}`}
          >
            {size}
          </button>
        ))}
      </div>
    </div>
  );
};

// Color Selector Component
const ColorSelector = ({ colors, selectedColor, onSelect }) => {
  if (!colors || colors.length === 0) return null;
  
  return (
    <div className="space-y-3" data-testid="color-selector">
      <h4 className="font-semibold text-sm" style={{ fontFamily: 'Archivo, sans-serif' }}>
        Color <span className="text-destructive">*</span>
        {selectedColor && <span className="font-normal text-muted-foreground ml-2">- {selectedColor}</span>}
      </h4>
      <div className="flex flex-wrap gap-3">
        {colors.map((color) => (
          <button
            key={color.name}
            onClick={() => onSelect(color.name)}
            className={`relative w-10 h-10 rounded-full border-2 transition-all ${
              selectedColor === color.name
                ? 'border-foreground ring-2 ring-foreground ring-offset-2'
                : 'border-border hover:border-foreground/50'
            }`}
            style={{ backgroundColor: color.hex }}
            title={color.name}
            data-testid={`color-option-${color.name}`}
          >
            {selectedColor === color.name && (
              <Check 
                className={`absolute inset-0 m-auto w-5 h-5 ${
                  color.hex === '#FFFFFF' || color.hex === '#ffffff' 
                    ? 'text-gray-800' 
                    : 'text-white'
                }`}
              />
            )}
            {/* White color border enhancement */}
            {(color.hex === '#FFFFFF' || color.hex === '#ffffff') && (
              <span className="absolute inset-0 rounded-full border border-gray-200" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// Physical Product Actions Component
const PhysicalProductActions = ({ product, quantity, setQuantity, onAddToCart, loading, selectedSize, setSelectedSize, selectedColor, setSelectedColor }) => {
  const hasVariants = product.has_variants;
  const availableSizes = product.available_sizes || [];
  const availableColors = product.available_colors || [];
  
  // Check if required variants are selected
  const canAddToCart = () => {
    if (!hasVariants) return product.stock > 0;
    const needsSize = availableSizes.length > 0;
    const needsColor = availableColors.length > 0;
    
    if (needsSize && !selectedSize) return false;
    if (needsColor && !selectedColor) return false;
    return product.stock > 0;
  };
  
  return (
    <div className="space-y-4">
      {/* Variant Selectors */}
      {hasVariants && (
        <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
          <SizeSelector 
            sizes={availableSizes} 
            selectedSize={selectedSize} 
            onSelect={setSelectedSize}
          />
          <ColorSelector 
            colors={availableColors} 
            selectedColor={selectedColor} 
            onSelect={setSelectedColor}
          />
        </div>
      )}
      
      {product.stock !== undefined && (
        <div className="flex items-center gap-2 text-sm">
          <span className={`w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-[#22c55e]' : 'bg-destructive'}`}></span>
          <span className={product.stock > 0 ? 'text-[#22c55e]' : 'text-destructive'}>
            {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
          </span>
        </div>
      )}
      
      <div className="flex items-center gap-4">
        <QuantitySelector quantity={quantity} setQuantity={setQuantity} />
        
        <Button 
          size="lg" 
          className="flex-1 gap-2 bg-[#2563EB] hover:bg-[#2563EB]/90"
          onClick={onAddToCart}
          disabled={loading || !canAddToCart()}
          data-testid="add-to-cart-btn"
        >
          <ShoppingCart className="w-5 h-5" />
          {hasVariants && (!selectedSize && availableSizes.length > 0 || !selectedColor && availableColors.length > 0) 
            ? 'Select Options' 
            : 'Add to Cart'}
        </Button>
      </div>
      
      {/* Variant selection reminder */}
      {hasVariants && !canAddToCart() && product.stock > 0 && (
        <p className="text-sm text-amber-600 text-center">
          Please select {!selectedSize && availableSizes.length > 0 ? 'a size' : ''} 
          {!selectedSize && availableSizes.length > 0 && !selectedColor && availableColors.length > 0 ? ' and ' : ''}
          {!selectedColor && availableColors.length > 0 ? 'a color' : ''} to continue
        </p>
      )}
    </div>
  );
};

// Affiliate Product Actions Component
const AffiliateProductActions = ({ onAffiliateClick, loading }) => (
  <div className="space-y-4">
    <div className="ticket-card p-6 bg-[#F97316]/5">
      <div className="text-center space-y-3">
        <Zap className="w-8 h-8 text-[#F97316] mx-auto" />
        <div>
          <h4 className="font-semibold" style={{ fontFamily: 'Archivo, sans-serif' }}>Affiliate Deal</h4>
          <p className="text-sm text-muted-foreground">
            Click below to visit our partner and get this exclusive deal
          </p>
        </div>
      </div>
    </div>
    
    <Button 
      size="lg" 
      className="w-full gap-2 bg-[#F97316] hover:bg-[#F97316]/90"
      onClick={onAffiliateClick}
      disabled={loading}
      data-testid="get-deal-btn"
    >
      <ExternalLink className="w-5 h-5" />
      {loading ? 'Loading...' : 'Get This Deal'}
    </Button>
    
    <p className="text-xs text-center text-muted-foreground">
      You will be redirected to our partner&apos;s website
    </p>
  </div>
);

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addToCart, loading: cartLoading } = useCart();
  const { formatPrice, formatShipping, calculateTotal, countryInfo } = useCurrency();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [affiliateLoading, setAffiliateLoading] = useState(false);

  const fetchProduct = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (err) {
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Reset variant selections when product changes
  useEffect(() => {
    setSelectedSize(null);
    setSelectedColor(null);
  }, [id]);

  const handleAddToCart = useCallback(async () => {
    if (!product) return;
    
    // For products with variants, validate selection
    if (product.has_variants) {
      const availableSizes = product.available_sizes || [];
      const availableColors = product.available_colors || [];
      
      if (availableSizes.length > 0 && !selectedSize) {
        toast.error('Please select a size');
        return;
      }
      if (availableColors.length > 0 && !selectedColor) {
        toast.error('Please select a color');
        return;
      }
    }
    
    const result = await addToCart(product.id, quantity, selectedSize, selectedColor);
    if (result.success) {
      const variantInfo = [];
      if (selectedSize) variantInfo.push(selectedSize);
      if (selectedColor) variantInfo.push(selectedColor);
      const variantText = variantInfo.length > 0 ? ` (${variantInfo.join(', ')})` : '';
      toast.success(`${product.name}${variantText} added to cart`);
    } else {
      toast.error(result.error);
    }
  }, [addToCart, product, quantity, selectedSize, selectedColor]);

  const handleAffiliateClick = useCallback(async () => {
    if (!product?.affiliate_url) return;
    
    setAffiliateLoading(true);
    try {
      // Track click and get affiliate URL with tracking params
      const response = await axios.post(`${API}/affiliate-tracking/track-click`, {
        product_id: product.id
      });
      
      const { affiliate_url } = response.data;
      
      // Open the tracked affiliate URL
      window.open(affiliate_url, '_blank');
    } catch (error) {
      console.error('Failed to track click:', error);
      // Fallback to direct URL if tracking fails
      window.open(product.affiliate_url, '_blank');
    } finally {
      setAffiliateLoading(false);
    }
  }, [product]);

  const handleShare = useCallback(async () => {
    if (!product) return;
    
    const shareUrl = window.location.href;
    const shareData = {
      title: product.name,
      text: `Check out ${product.name} - $${product.price.toFixed(2)} on SALESSPACE!`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    }
  }, [product]);

  if (loading) return <ProductSkeleton />;
  if (!product) return <ProductNotFound />;

  const isPhysical = product.product_type === 'physical';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-slide-up">
      {/* Breadcrumb */}
      <div className="mb-8">
        <Link 
          to="/products" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Product Media (Images + Videos) */}
        <ProductMediaGallery product={product} isPhysical={isPhysical} />

        {/* Product Info */}
        <div className="space-y-6">
          <div>
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              {product.category}
            </span>
            <h1 
              className="text-3xl sm:text-4xl font-bold mt-2" 
              style={{ fontFamily: 'Archivo, sans-serif' }}
              data-testid="product-name"
            >
              {product.name}
            </h1>
          </div>

          <div className="space-y-2">
            <div className="flex items-baseline gap-4">
              <span 
                className="text-4xl font-bold" 
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
                data-testid="product-price"
              >
                {formatPrice(product.price)}
              </span>
              {/* Commission info hidden from customers - only visible in Admin */}
              <WishlistButton
                product={product}
                className="ml-auto h-10 w-10 border bg-background hover:bg-muted"
                iconClass="w-5 h-5"
                testId="wishlist-toggle-detail"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={handleShare}
                data-testid="share-product-btn"
              >
                <Share2 className="w-5 h-5" />
              </Button>
            </div>

            <WhatsAppContactButton product={product} />
            
            {/* Shipping Info */}
            {isPhysical && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Truck className="w-4 h-4" />
                <span>+{formatShipping()} shipping to {countryInfo.flag} {countryInfo.name}</span>
              </div>
            )}
            
            {/* Total with shipping */}
            {isPhysical && (
              <div className="bg-muted/50 rounded-lg p-3 mt-2">
                <div className="flex justify-between text-sm mb-1">
                  <span>Product ({quantity}x)</span>
                  <span>{formatPrice(product.price * quantity)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Shipping to {countryInfo.flag}</span>
                  <span>{formatShipping()}</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold">
                  <span>Estimated Total</span>
                  <span className="text-lg">{calculateTotal(product.price, quantity).formatted.total}</span>
                </div>
              </div>
            )}
          </div>

          <Separator />

          <div>
            <h3 className="font-semibold mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>Description</h3>
            <p className="text-muted-foreground leading-relaxed" data-testid="product-description">
              {product.description}
            </p>
          </div>

          {product.tags && product.tags.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>Tags</h3>
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Actions */}
          {isPhysical ? (
            <PhysicalProductActions 
              product={product}
              quantity={quantity}
              setQuantity={setQuantity}
              onAddToCart={handleAddToCart}
              loading={cartLoading}
              selectedSize={selectedSize}
              setSelectedSize={setSelectedSize}
              selectedColor={selectedColor}
              setSelectedColor={setSelectedColor}
            />
          ) : (
            <AffiliateProductActions onAffiliateClick={handleAffiliateClick} loading={affiliateLoading} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
